import * as coops from './sources/coops';
import * as ndbc from './sources/ndbc';
import * as goldenGate from './sources/goldenGate';
import { STATIONS } from './stations';
import type { Point, Reading, Station, StationHistory, StationState } from './types';
import type { MetricKey } from './lib/mapMetric';

/** Dispatch on source so nothing downstream has to know where a reading came from. */
function driver(station: Station) {
  if (station.source === 'konect') return goldenGate;
  return station.source === 'coops' ? coops : ndbc;
}

export function initialState(): Map<string, StationState> {
  return new Map(
    STATIONS.map((station) => [
      station.id,
      { station, latest: null, history: null, error: null, loading: true },
    ]),
  );
}

/**
 * Refreshes every station concurrently, invoking `onStation` as each settles so
 * markers appear progressively rather than after the slowest request.
 * Never rejects — a station that fails records its error and the rest continue.
 *
 * The CO-OPS stations are fetched one apiece, straight from the browser to NOAA.
 * The offshore buoys go through our own Worker, so they are fetched as a single
 * batch: they are the only requests that spend a shared daily quota, and there
 * are four of them on every page load.
 */
export async function refreshAll(
  states: Map<string, StationState>,
  onStation: (state: StationState) => void,
): Promise<void> {
  const settle = (state: StationState, reading: Reading | null, error: unknown) => {
    if (reading) {
      state.latest = reading;
      state.error = null;
    } else {
      // Keep any previous reading on screen; it gets an age badge rather than vanishing.
      state.error = error instanceof Error ? error.message : String(error);
    }
    state.loading = false;
    onStation(state);
  };

  const offshore = STATIONS.filter((s) => s.source === 'ndbc');
  const inshore = STATIONS.filter((s) => s.source !== 'ndbc');

  for (const station of STATIONS) states.get(station.id)!.loading = true;

  const offshoreTask = (async () => {
    if (offshore.length === 0) return;
    let found = new Map<string, Reading>();
    let failure: unknown = new Error('No current observation');
    try {
      found = await ndbc.latestAll(offshore.map((s) => s.id));
    } catch (err) {
      // The whole batch failed; every buoy reports the same reason.
      failure = err;
    }
    for (const station of offshore) {
      settle(states.get(station.id)!, found.get(station.id) ?? null, failure);
    }
  })();

  const inshoreTasks = inshore.map(async (station) => {
    const state = states.get(station.id)!;
    try {
      settle(state, await driver(station).latest(station.id), null);
    } catch (err) {
      settle(state, null, err);
    }
  });

  await Promise.all([offshoreTask, ...inshoreTasks]);
}

/**
 * The current value of one metric for every station that reports it.
 *
 * Only called while a sensor filter is narrowing the map, which is the point:
 * most visits never ask for this and pay nothing. The offshore buoys cost no
 * request at all — their values arrive inside the wind batch — so the traffic
 * is a handful of direct browser-to-NOAA calls for the CO-OPS stations.
 *
 * Never rejects. A station whose sensor is silent is simply absent from the map,
 * and its label shows a dash rather than a stale number.
 */
export async function loadMetric(
  metric: MetricKey,
  stations: Station[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();

  await Promise.all(
    stations.map(async (station) => {
      try {
        // Through the driver rather than a source test, so a station from a
        // third feed cannot fall down the CO-OPS path and ask for an id that
        // service has never heard of.
        const value = await driver(station).latestMetric(station.id, metric);
        if (value !== null && Number.isFinite(value)) out.set(station.id, value);
      } catch {
        /* One silent sensor must not blank the others. */
      }
    }),
  );

  return out;
}

/**
 * Fetched only when a popup opens — never for all 18 stations up front.
 *
 * Wind is required; every other series is best-effort. A station whose water
 * level sensor is down still gets its wind chart, just without that panel.
 */
export async function loadHistory(state: StationState, hours = 24): Promise<StationHistory> {
  if (state.history) return state.history;

  const { station } = state;
  const { sensors } = station;

  const wind = await driver(station).history(station.id, hours);
  const history: StationHistory = { wind };

  // Adopt the newest reading from the history we just fetched, if it beats what
  // the marker was drawn from. A national station is drawn from NDBC's hourly
  // rollup but its history comes from realtime2 every ten minutes, so without
  // this the panel shows a figure 37 minutes old above a chart that runs to
  // five — its own headline lagging its own graph.
  const newest = wind[wind.length - 1];
  if (newest && (state.latest === null || newest.t > state.latest.t)) {
    state.latest = newest;
  }

  if (station.source === 'konect') {
    // Wind only. Its other columns live on a second table, which is a second
    // request to someone else's service for numbers the Bay already reports.
  } else if (station.source === 'ndbc') {
    // Already in the file that `history()` just downloaded — no extra requests.
    if (sensors.waves) history.waveHeight = ndbc.waveHeight(station.id);
    if (sensors.waterTemp) history.waterTemp = ndbc.waterTemp(station.id);
    if (sensors.airTemp) history.airTemp = ndbc.airTemp(station.id);
  } else {
    const wanted: Array<[keyof StationHistory, () => Promise<Point[]>]> = [];
    if (sensors.waterLevel) {
      wanted.push(['waterLevel', () => coops.waterLevel(station.id, hours)]);
      wanted.push(['waterLevelPredicted', () => coops.waterLevelPredicted(station.id, hours)]);
    }
    if (sensors.waterTemp) wanted.push(['waterTemp', () => coops.waterTemp(station.id, hours)]);
    if (sensors.airTemp) wanted.push(['airTemp', () => coops.airTemp(station.id, hours)]);

    const settled = await Promise.allSettled(wanted.map(([, fn]) => fn()));
    settled.forEach((result, i) => {
      if (result.status === 'fulfilled' && result.value.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (history as any)[wanted[i][0]] = result.value;
      }
    });
  }

  state.history = history;
  return history;
}
